/* eslint-disable max-lines */
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';

import {
  dropdownMenuCheckItemClassesExperimental,
  dropdownMenuItemVariantsExperimental,
  dropdownMenuLabelClassesExperimental,
  dropdownMenuSeparatorClassesExperimental,
  dropdownMenuSubTriggerClassesExperimental,
} from './dropdown-menu.experimental.styles';
import { dropdownMenuItemVariants } from './dropdown-menu.styles';

import type {
  DropdownMenuCheckboxItemProps,
  DropdownMenuContentProps,
  DropdownMenuItemDescriptionProps,
  DropdownMenuItemProps,
  DropdownMenuLabelProps,
  DropdownMenuRadioItemProps,
  DropdownMenuSeparatorProps,
  DropdownMenuShortcutProps,
  DropdownMenuSubContentProps,
  DropdownMenuSubTriggerProps,
} from './dropdown-menu.types';

import { TypographyP2 } from '../typography';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(
  (
    { 'data-testid': testId, className, inset, children, hideChevron = false, ...props },
    ref
  ) => {
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    return (
      <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        data-testid={testId}
        data-experimental={isExperimentalForms || undefined}
        className={cn(
          isExperimentalForms
            ? dropdownMenuSubTriggerClassesExperimental
            : "flex cursor-default select-none items-center gap-2 rounded-md p-2 text-sm outline-none focus:bg-surface-hover data-[state=open]:bg-surface-hover [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
          inset && 'ps-8',
          className
        )}
        {...props}
      >
        {children}
        {!hideChevron && <ChevronRight className="ms-auto size-4 rtl:-scale-x-100" />}
      </DropdownMenuPrimitive.SubTrigger>
    );
  }
);
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ 'data-testid': testId, className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    data-testid={testId}
    className={cn(
      'z-50 min-w-32 origin-[var(--radix-dropdown-menu-content-transform-origin)] overflow-hidden rounded-lg border-0 bg-surface-elevated p-1 text-text-primary shadow-popover-elevated ring-1 ring-surface-elevated-border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(
  (
    { 'data-testid': testId, className, sideOffset = 4, align = 'center', ...props },
    ref
  ) => {
    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={ref}
          data-testid={testId}
          sideOffset={sideOffset}
          align={align}
          className={cn(
            'z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-32 origin-[var(--radix-dropdown-menu-content-transform-origin)] overflow-y-auto overflow-x-hidden rounded-lg border-0 bg-surface-elevated p-1 text-text-primary shadow-popover ring-1 ring-surface-elevated-border data-[state=closed]:overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className
          )}
          {...props}
        />
      </DropdownMenuPrimitive.Portal>
    );
  }
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(
  (
    {
      'data-testid': testId,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
      className,
      inset,
      selected = false,
      variant,
      description,
      children,
      ...props
    },
    ref
  ) => {
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    // Name the item from the label alone and expose the description via
    // aria-describedby — otherwise the description text folds into the name and
    // gets announced twice. Respect a caller-supplied name/description.
    const labelId = React.useId();
    const generatedDescriptionId = React.useId();
    const descriptionId = description ? generatedDescriptionId : undefined;
    const hasOwnName = Boolean(ariaLabel || ariaLabelledBy);
    const labelledBy = description && !hasOwnName ? labelId : ariaLabelledBy;
    const describedBy =
      [ariaDescribedBy, descriptionId].filter(Boolean).join(' ') || undefined;
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        data-testid={testId}
        data-experimental={isExperimentalForms || undefined}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className={cn(
          isExperimentalForms
            ? dropdownMenuItemVariantsExperimental({ variant, inset, selected })
            : cn(
                dropdownMenuItemVariants({ variant, inset, selected }),
                props.disabled && 'cursor-default'
              ),
          // `group` (unnamed) lets the description dim on item hover/focus; top-align
          // so the icon stays on the title line rather than centring on both lines.
          description && 'group items-start',
          className
        )}
        data-interactive
        {...props}
      >
        {description ? (
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div id={labelId} className="flex items-center gap-2">
              {children}
            </div>
            <DropdownMenuItemDescription id={descriptionId} className="ps-6 text-xs">
              {description}
            </DropdownMenuItemDescription>
          </div>
        ) : (
          children
        )}
      </DropdownMenuPrimitive.Item>
    );
  }
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(({ 'data-testid': testId, className, children, checked, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      data-testid={testId}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? dropdownMenuCheckItemClassesExperimental
          : 'relative flex cursor-default select-none items-center rounded-md py-1.5 pe-8 ps-2 text-sm outline-none focus:bg-surface-hover focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>span]:pointer-events-none [&>span]:end-2 [&>span]:start-auto',
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute start-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(({ 'data-testid': testId, className, children, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      data-testid={testId}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? dropdownMenuCheckItemClassesExperimental
          : 'relative flex cursor-default select-none items-center rounded-md py-1.5 pe-8 ps-2 text-sm outline-none focus:bg-surface-hover focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>span]:pointer-events-none [&>span]:end-2 [&>span]:start-auto',
        className
      )}
      {...props}
    >
      <span className="absolute start-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>(({ 'data-testid': testId, className, inset, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      data-testid={testId}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? dropdownMenuLabelClassesExperimental
          : 'px-2 py-1.5 text-sm font-semibold',
        inset && 'ps-8',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  DropdownMenuSeparatorProps
>(({ 'data-testid': testId, className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      data-testid={testId}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? dropdownMenuSeparatorClassesExperimental
          : '-mx-1 my-1 h-px bg-background-secondary',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = React.forwardRef<HTMLSpanElement, DropdownMenuShortcutProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <span
      ref={ref}
      data-testid={testId}
      className={cn(
        'ms-auto text-xs tracking-widest text-text-tertiary group-hover/dropdown-menu-item:text-text-secondary group-focus/dropdown-menu-item:text-text-secondary',
        className
      )}
      {...props}
    />
  )
);
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

const DropdownMenuItemDescription = React.forwardRef<
  HTMLParagraphElement,
  DropdownMenuItemDescriptionProps
>(({ 'data-testid': testId, className, inset, ...props }, ref) => {
  return (
    <TypographyP2
      ref={ref}
      data-testid={testId}
      className={cn(
        'text-text-tertiary group-hover:text-text-secondary group-focus-visible:text-text-secondary',
        inset && 'ps-7',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuItemDescription.displayName = 'DropdownMenuItemDescription';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemDescription,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
